/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * Rocket.net API
 * Rocket.net - All APIs
 * OpenAPI spec version: 1.0.0
 */
import { useMutation } from '@tanstack/react-query';
import type {
  MutationFunction,
  QueryClient,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';

import { customFetch } from '../../custom-fetch';
import { addMetaToMutation } from '../../mutators/add-meta';
import type {
  CreateCustomerSiteTransferRequest,
  CustomerSiteTransferResponse,
  Error,
  SiteID,
  TransferID,
} from '../rocketNetAPI.schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

/**
 * Starts transferring site ownership to another Rocket.net account by the recipient's email. Account owner only.
 * @summary Transfer a site to another account
 */
export const transferSite = (
  id: SiteID,
  createCustomerSiteTransferRequest: CreateCustomerSiteTransferRequest,
  signal?: AbortSignal
) => {
  return customFetch<CustomerSiteTransferResponse>({
    url: `/v1/sites/${id}/transfer`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createCustomerSiteTransferRequest,
    signal,
  });
};

export const getTransferSiteMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof transferSite>>,
    TError,
    { id: SiteID; data: CreateCustomerSiteTransferRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof transferSite>>,
  TError,
  { id: SiteID; data: CreateCustomerSiteTransferRequest },
  TContext
> => {
  const mutationKey = ['transferSite'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof transferSite>>,
    { id: SiteID; data: CreateCustomerSiteTransferRequest }
  > = props => {
    const { id, data } = props ?? {};

    return transferSite(id, data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/{id}/transfer` },
    {
      operationId: 'app.controllers.customer_site_transfers_controller.transfer_site',
      operationName: 'transferSite',
    }
  );

  return customOptions;
};

export type TransferSiteMutationResult = NonNullable<Awaited<ReturnType<typeof transferSite>>>;
export type TransferSiteMutationBody = CreateCustomerSiteTransferRequest;
export type TransferSiteMutationError = Error;

/**
 * @summary Transfer a site to another account
 */
export const useTransferSite = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof transferSite>>,
      TError,
      { id: SiteID; data: CreateCustomerSiteTransferRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof transferSite>>,
  TError,
  { id: SiteID; data: CreateCustomerSiteTransferRequest },
  TContext
> => {
  const mutationOptions = getTransferSiteMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Accepts a site being transferred into your account. May require a plan upgrade if you are at your site limit.
 * @summary Accept an incoming site transfer
 */
export const acceptSiteTransfer = (id: SiteID, transferId: TransferID, signal?: AbortSignal) => {
  return customFetch<CustomerSiteTransferResponse>({
    url: `/v1/sites/${id}/transfer/${transferId}/accept`,
    method: 'POST',
    signal,
  });
};

export const getAcceptSiteTransferMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof acceptSiteTransfer>>,
    TError,
    { id: SiteID; transferId: TransferID },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof acceptSiteTransfer>>,
  TError,
  { id: SiteID; transferId: TransferID },
  TContext
> => {
  const mutationKey = ['acceptSiteTransfer'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof acceptSiteTransfer>>,
    { id: SiteID; transferId: TransferID }
  > = props => {
    const { id, transferId } = props ?? {};

    return acceptSiteTransfer(id, transferId);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/{id}/transfer/{transferId}/accept` },
    {
      operationId: 'app.controllers.customer_site_transfers_controller.accept_site_transfer',
      operationName: 'acceptSiteTransfer',
    }
  );

  return customOptions;
};

export type AcceptSiteTransferMutationResult = NonNullable<
  Awaited<ReturnType<typeof acceptSiteTransfer>>
>;

export type AcceptSiteTransferMutationError = Error;

/**
 * @summary Accept an incoming site transfer
 */
export const useAcceptSiteTransfer = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof acceptSiteTransfer>>,
      TError,
      { id: SiteID; transferId: TransferID },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof acceptSiteTransfer>>,
  TError,
  { id: SiteID; transferId: TransferID },
  TContext
> => {
  const mutationOptions = getAcceptSiteTransferMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Cancels a pending transfer you initiated before it is accepted.
 * @summary Cancel a site transfer
 */
export const cancelSiteTransfer = (id: SiteID, transferId: TransferID, signal?: AbortSignal) => {
  return customFetch<CustomerSiteTransferResponse>({
    url: `/v1/sites/${id}/transfer/${transferId}/cancel`,
    method: 'POST',
    signal,
  });
};

export const getCancelSiteTransferMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof cancelSiteTransfer>>,
    TError,
    { id: SiteID; transferId: TransferID },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof cancelSiteTransfer>>,
  TError,
  { id: SiteID; transferId: TransferID },
  TContext
> => {
  const mutationKey = ['cancelSiteTransfer'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof cancelSiteTransfer>>,
    { id: SiteID; transferId: TransferID }
  > = props => {
    const { id, transferId } = props ?? {};

    return cancelSiteTransfer(id, transferId);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/{id}/transfer/{transferId}/cancel` },
    {
      operationId: 'app.controllers.customer_site_transfers_controller.cancel_site_transfer',
      operationName: 'cancelSiteTransfer',
    }
  );

  return customOptions;
};

export type CancelSiteTransferMutationResult = NonNullable<
  Awaited<ReturnType<typeof cancelSiteTransfer>>
>;

export type CancelSiteTransferMutationError = Error;

/**
 * @summary Cancel a site transfer
 */
export const useCancelSiteTransfer = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof cancelSiteTransfer>>,
      TError,
      { id: SiteID; transferId: TransferID },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof cancelSiteTransfer>>,
  TError,
  { id: SiteID; transferId: TransferID },
  TContext
> => {
  const mutationOptions = getCancelSiteTransferMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Declines a site transfer offered to your account.
 * @summary Decline a site transfer
 */
export const rejectSiteTransfer = (id: SiteID, transferId: TransferID, signal?: AbortSignal) => {
  return customFetch<CustomerSiteTransferResponse>({
    url: `/v1/sites/${id}/transfer/${transferId}/reject`,
    method: 'POST',
    signal,
  });
};

export const getRejectSiteTransferMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof rejectSiteTransfer>>,
    TError,
    { id: SiteID; transferId: TransferID },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof rejectSiteTransfer>>,
  TError,
  { id: SiteID; transferId: TransferID },
  TContext
> => {
  const mutationKey = ['rejectSiteTransfer'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof rejectSiteTransfer>>,
    { id: SiteID; transferId: TransferID }
  > = props => {
    const { id, transferId } = props ?? {};

    return rejectSiteTransfer(id, transferId);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/{id}/transfer/{transferId}/reject` },
    {
      operationId: 'app.controllers.customer_site_transfers_controller.reject_site_transfer',
      operationName: 'rejectSiteTransfer',
    }
  );

  return customOptions;
};

export type RejectSiteTransferMutationResult = NonNullable<
  Awaited<ReturnType<typeof rejectSiteTransfer>>
>;

export type RejectSiteTransferMutationError = Error;

/**
 * @summary Decline a site transfer
 */
export const useRejectSiteTransfer = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof rejectSiteTransfer>>,
      TError,
      { id: SiteID; transferId: TransferID },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof rejectSiteTransfer>>,
  TError,
  { id: SiteID; transferId: TransferID },
  TContext
> => {
  const mutationOptions = getRejectSiteTransferMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
